import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { TranslationKeys } from '../../i18n/types.js';
import type { UseMultiplayerReturn } from '../../hooks/useMultiplayer.js';
import type { StudioCreateRequest } from './useStudioCommands.js';

/** What happened to the GM's own last «show» attempt, next to the map's button. */
export interface ShowAttempt {
  readonly pageId: string;
  readonly pending: boolean;
  readonly note?: string;
}

interface Deps {
  /** A quiet useStudioCommands: the refusal is shown next to the action. */
  readonly create: (request: StudioCreateRequest) => void;
  readonly t: (key: keyof TranslationKeys) => string;
  /** The map still exists (it may be deleted while the dialog is open). */
  readonly pageExists: (pageId: string) => boolean;
  /** The players' map now — the confirmed activePageId. */
  readonly activePageId: () => string;
  /** A map's name for the message after the show. */
  readonly nameOf: (pageId: string) => string | undefined;
  /** «Back to the previous map» from that message is sent on the live socket. */
  readonly mp: Pick<UseMultiplayerReturn, 'send' | 'status'>;
  /** Runs right before page:activate (the table keeps the GM's unsaved canvas first). */
  readonly beforeShow?: () => void;
  /** GK-5: how many of the players' characters stand on the map now (0 / absent — no «carry» choice). */
  readonly partyCount?: () => number;
}

/** How long the message after a show (with «back to the previous map») stays. */
export const SHOWN_MESSAGE_MS = 15_000;

/**
 * «Show to players…» — every GM surface (the strip over the map, the page
 * list, the inspector, the game plan, beat maps in Studio): ask → a
 * confirmation that names the map → the existing page:activate. Success is
 * only the confirmed page:activated of that map; a refusal or a lost answer
 * is a note, never a re-send — the players' map must not change behind the
 * GM's back. One attempt at a time.
 *
 * After the confirmed show: a message «Players see …» with «Back to …» —
 * page:activate of the previous map with undoShowOf: the server checks the
 * players are still on the shown map and takes back the untouched transition
 * draft of that show (table-map-flow T0). The action works after the surface
 * that showed the map is gone (a sidebar tab switched): it sends on the live
 * socket; a refusal comes as any command:error of that surface.
 */
export function useShowMapToPlayers(deps: Deps): {
  readonly attempt: ShowAttempt | undefined;
  /** The map awaiting the GM's confirmation. */
  readonly confirmId: string | undefined;
  readonly ask: (pageId: string) => void;
  readonly cancel: () => void;
  /** Show the map alone (the only «Show» when nobody stands on the players' map; «Map only» otherwise). */
  readonly confirm: () => void;
  /**
   * GK-5: the players' characters stand on their map now — the confirmation offers «Show with the characters (N)»
   * (confirmWithParty) next to «Map only» (confirm). undefined — nobody to carry, one «Show».
   */
  readonly carry: { readonly count: number; readonly confirmWithParty: () => void } | undefined;
} {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const [attempt, setAttempt] = useState<ShowAttempt | undefined>(undefined);
  const pendingRef = useRef(false);
  const [confirmId, setConfirmId] = useState<string | undefined>(undefined);
  const shownToastRef = useRef<string | number | undefined>(undefined);

  const back = useCallback((shownId: string, fromId: string): void => {
    const { t, mp, pageExists, activePageId } = depsRef.current;
    if (!pageExists(fromId) || activePageId() !== shownId) return;
    if (mp.status !== 'connected') {
      toast.error(t('session.connectionLost'));
      return;
    }
    mp.send({ type: 'page:activate', pageId: fromId, undoShowOf: shownId } as never);
  }, []);

  /** carried: the characters were offered — how many the server really moved (0 — the map went without them). */
  const announce = useCallback((shownId: string, fromId: string, carried: number | undefined): void => {
    const { t, nameOf, pageExists } = depsRef.current;
    if (shownToastRef.current !== undefined) toast.dismiss(shownToastRef.current);
    const fromName = nameOf(fromId);
    const party = carried === undefined
      ? undefined
      : carried > 0 ? t('mapView.shownCarried').replace('{n}', String(carried)) : t('mapView.shownWithoutParty');
    shownToastRef.current = toast.success(t('mapView.shown').replace('{name}', nameOf(shownId) ?? ''), {
      description: party === undefined ? t('mapView.shownDraft') : `${party}. ${t('mapView.shownDraft')}`,
      duration: SHOWN_MESSAGE_MS,
      ...(fromName !== undefined && pageExists(fromId)
        ? { action: { label: t('mapView.backTo').replace('{name}', fromName), onClick: () => back(shownId, fromId) } }
        : {}),
    });
  }, [back]);

  /** offered: the confirmation offered the characters; carry: the GM chose «with the characters». */
  const show = useCallback((pageId: string, fromId: string, offered: boolean, carry: boolean): void => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    setAttempt({ pageId, pending: true });
    const { t } = depsRef.current;
    const refused = (reason: string): string => t('plan.refused').replace('{reason}', reason);
    const settle = (note?: string): void => {
      pendingRef.current = false;
      setAttempt(note === undefined ? undefined : { pageId, pending: false, note });
    };
    depsRef.current.beforeShow?.();
    // What the server really moved (its page:activated to the GMs says it when asked to carry).
    let carried: number | undefined;
    depsRef.current.create({
      command: 'page:activate',
      echo: 'page:activated',
      name: t('plan.map.show'),
      payload: carry ? { pageId, carryParty: true } : { pageId },
      // page:activated carries no requestId; the confirmed activation of this page is the answer.
      matches: (echo) => {
        if (echo.pageId !== pageId) return undefined;
        carried = typeof echo.carried === 'number' ? echo.carried : undefined;
        return pageId;
      },
      onCreated: () => {
        settle();
        announce(pageId, fromId, offered ? (carry ? (carried ?? 0) : 0) : undefined);
      },
      onFailed: (reason) => settle(refused(reason)),
      onUnknown: () => settle(t('plan.lostAnswer')),
    });
  }, [announce]);

  const ask = useCallback((pageId: string): void => setConfirmId(pageId), []);
  const cancel = useCallback((): void => setConfirmId(undefined), []);
  const decide = useCallback((withParty: boolean): void => {
    const pageId = confirmId;
    setConfirmId(undefined);
    if (pageId === undefined || !depsRef.current.pageExists(pageId)) return;
    // Already the players' map (another GM showed it meanwhile): nothing to send.
    const fromId = depsRef.current.activePageId();
    if (pageId === fromId) return;
    const offered = (depsRef.current.partyCount?.() ?? 0) > 0;
    show(pageId, fromId, offered, withParty && offered);
  }, [confirmId, show]);
  const confirm = useCallback((): void => decide(false), [decide]);
  const confirmWithParty = useCallback((): void => decide(true), [decide]);

  const count = confirmId !== undefined ? (depsRef.current.partyCount?.() ?? 0) : 0;
  const carry = count > 0 ? { count, confirmWithParty } : undefined;
  return { attempt, confirmId, ask, cancel, confirm, carry };
}
